import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import CompanyGroup from './CompanyGroup';
import Link from 'components/Link';
import { IconButton } from 'components/buttons';
import { useTranslation } from 'hooks';
import {
  ButtonWrapper,
  Button,
  Label,
  ToggleIcon,
  ExpandCollapseContainer
} from 'components/Resume/style';
import { ArrowLeft, ArrowRight } from 'components/icons';
import { filter, each } from 'lodash';

function Details({ editPath, indexedMeta, language, workExperience, expandCollapse, handleCollapseExpand }) {
  const { tr } = useTranslation();
  const location = useLocation();

  const addPathState = useMemo(() => ({ background: location }), [location]);

  // group workExperience
  let filteredWork = workExperience.sort(
    (a, b) => new Date(b.dateFrom).getTime() - new Date(a.dateFrom).getTime()
  );

  let result = filteredWork.reduce((r, a) => {
    let index = r.findIndex(v => v.companyGroupName === a.companyGroupName);
    let companyGroup = {};
    if (index < 0) {
      companyGroup = {
        companyGroupName: a.companyGroupName,
        companyGroupId: a.companyGroupId,
        companyGroupCountry: a.companyGroupCountry,
        companies: [],
        dateFrom: a.dateFrom,
        isActualEngagement: a.dateTo === '' ? true : a.isActualEngagement,
        industry: a.industry,
        dateTo: a.dateTo === '' ? '' : a.dateTo
      };
      r.push(companyGroup);
    } else {
      companyGroup = r[index];
      if (a.dateFrom < companyGroup.dateFrom) companyGroup.dateFrom = a.dateFrom;
      if (a.dateTo === '') {
        companyGroup.dateTo = '';
        companyGroup.isActualEngagement = true;
      } else if (companyGroup.dateTo !== '' && a.dateTo > companyGroup.dateTo) {
        companyGroup.dateTo = a.dateTo;
      }
    }

    let cindex = companyGroup.companies.findIndex(v => v.compId === a.companyId);
    if (cindex < 0) {
      companyGroup.companies.push({
        companyName: a.companyName,
        compId: a.companyId,
        experiences: [a],
        dateFrom: a.dateFrom,
        isActualEngagement: a.dateTo === '' ? true : a.isActualEngagement,
        industry: a.industry,
        dateTo: a.dateTo === '' ? '' : a.dateTo,
        city: a.city,
        companyCountry: a.country,
        industryDescription: a.industryDescription,
        ownershipStructure: a.ownershipStructure,
        companyEmployees: a.companyEmployees,
        companyTurnover: a.companyTurnover
      });
    } else {
      const obj = companyGroup.companies[cindex];
      obj.experiences.push(a);
      if (a.dateFrom < obj.dateFrom) obj.dateFrom = a.dateFrom;
      if (a.dateTo === '') {
        obj.dateTo = '';
        obj.isActualEngagement = true;
      } else if (obj.dateTo !== '' && a.dateTo > obj.dateTo) {
        obj.dateTo = a.dateTo;
      }
    }
    return r;
  }, []);

  let nonGroupedComp = [];
  each(result, group => {
    if (group.companyGroupName === '') {
      nonGroupedComp = group.companies;
    }
  });

  result = filter(result, val => val.companyGroupName !== '');

  each(nonGroupedComp, comp => {
    const companyTemplate = {
      companyGroupName: '',
      companyGroupId: '',
      companyGroupCountry: '',
      companies: [comp],
      dateFrom: comp.dateFrom,
      isActualEngagement: comp.dateTo === '' ? true : comp.isActualEngagement,
      industry: comp.industry,
      dateTo: comp.dateTo === '' ? '' : comp.dateTo
    };
    result.push(companyTemplate);
  });

  // sorting logic remains identical
  result.sort((a, b) => {
    const aOngoing = a.dateTo === '';
    const bOngoing = b.dateTo === '';

    if (aOngoing && bOngoing) return 0;
    if (aOngoing) return -1;
    if (bOngoing) return 1;
    return new Date(b.dateTo) - new Date(a.dateTo);
  });

  result.sort((a, b) => {
    if (a.dateTo === b.dateTo) return new Date(b.dateFrom) - new Date(a.dateFrom);
    return 0;
  });

  result.sort((a, b) => {
    if (a.dateTo === '' && new Date(a.dateFrom) > new Date(b.dateFrom)) return -1;
    if (b.dateTo === '' && new Date(a.dateFrom) < new Date(b.dateFrom)) return 1;
    return 0;
  });

  return (
    <React.Fragment>
      <ExpandCollapseContainer>
        <Button onClick={handleCollapseExpand}>
          <Label>{tr(expandCollapse ? 'button.hideAllDetails' : 'button.showAllDetails')}</Label>
          <ToggleIcon as={expandCollapse ? ArrowRight : ArrowLeft} />
        </Button>
      </ExpandCollapseContainer>

      {result.map((item, index) => (
        <CompanyGroup
        data={item}
        editPath={editPath}
        indexedMeta={indexedMeta}
        key={index}
        language={language}
        expandCollapse={expandCollapse}
      />

      ))}

      <ButtonWrapper>
        <div>
          <Link state={addPathState} to={editPath}>
            <span>{tr('title.addWorkExperience')}</span>
          </Link>
        </div>
        <IconButton
          className="wexp-add-bottom"
          component={Link}
          icon="Plus"
          state={addPathState}
          to={editPath}
        />
      </ButtonWrapper>
    </React.Fragment>
  );
}

Details.propTypes = {
  editPath: PropTypes.string,
  indexedMeta: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  workExperience: PropTypes.array.isRequired
};

export default Details;
